import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useTranslation } from "@coworker/locales";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { MediaCardProps } from "../../../components/MediaCard/MediaCard";
import { ArticleSearchPrompt } from "../../../components/ArticleSearchPrompt/ArticleSearchPrompt";
import { ProductCard } from "../../../components/ProductCard/ProductCard";
import mediaDetailPageStyles from "../../media-detail-page.module.css";
import { mediaDetailProducts } from "../../mock-media";

type ProductSelectionStepProps = {
  media: MediaCardProps;
  backupProducts: Record<string, Item>;
  onBackupProductsChange: Dispatch<SetStateAction<Record<string, Item>>>;
};
export const ProductSelectionStep = ({
  media,
  backupProducts,
  onBackupProductsChange,
}: ProductSelectionStepProps) => {
  const { t } = useTranslation();
  const products = useMemo(
    () => (media ? mediaDetailProducts(media.id) : []),
    [media]
  );

  const [searchingBackupFor, setSearchingBackupFor] = useState<string | null>(
    null
  );

  const [backupError, setBackupError] = useState("");

  const closeSearch = () => {
    setSearchingBackupFor(null);
    setBackupError("");
  };

  const handleSelectBackup = (item: Item) => {
    const alreadyUsed =
      products.some(({ item: product }) => product.no === item.no) ||
      Object.entries(backupProducts).some(
        ([articleId, { no }]) =>
          articleId !== searchingBackupFor && no === item.no
      );
    if (alreadyUsed) {
      setBackupError(t("articleAlreadyExistsString"));
      return;
    }

    if (searchingBackupFor) {
      onBackupProductsChange((current) => ({
        ...current,
        [searchingBackupFor]: item,
      }));
    }
    closeSearch();
  };

  const handleRemoveBackup = (articleId: string) =>
    onBackupProductsChange((current) => {
      const next = { ...current };
      delete next[articleId];
      return next;
    });

  return (
    <section className={mediaDetailPageStyles["field"]}>
      <FixaText tagName="h2" className={mediaDetailPageStyles["title"]}>
        {t("productSelectionString")}
      </FixaText>

      <div className={mediaDetailPageStyles["productsGrid"]}>
        {products.map(({ item, article, badges, saleStartDate }) => (
          <ProductCard
            key={article.id}
            item={item}
            article={article}
            badges={badges}
            saleStartDate={saleStartDate}
            backupProduct={backupProducts[article.id]}
            onSelectBackupProduct={() => setSearchingBackupFor(article.id)}
            onRemoveBackupProduct={() => handleRemoveBackup(article.id)}
          />
        ))}
      </div>

      <ArticleSearchPrompt
        title={t("selectBackupProductString")}
        isOpen={searchingBackupFor !== null}
        onClose={closeSearch}
        onSelect={handleSelectBackup}
        errorMessage={backupError}
      />
    </section>
  );
};
